import { Doughnut } from 'react-chartjs-2'

import {
  ArcElement,
  Chart as ChartJS,
  type ChartOptions,
  Legend,
  type Plugin,
  Tooltip,
  type TooltipItem
} from 'chart.js'

import { OTHERS_COLOR, PIE_CHART_COLORS } from './chartColors'

ChartJS.register(ArcElement, Legend, Tooltip)

const COLORS = PIE_CHART_COLORS

interface Props {
  items: {
    label: string
    value: number
  }[]
  centerLabel?: string
  tooltipTitle?: string
  maxMainItems?: number
  // 系列色を指定する（センチメント等、値の意味で色が決まる場合に使う）
  colors?: string[]
}

const PieChart = ({ items, centerLabel, tooltipTitle, maxMainItems = 4, colors }: Props) => {
  const palette = colors && colors.length > 0 ? colors : COLORS
  const total = items.reduce((sum, item) => sum + item.value, 0)
  const mainItems = items.slice(0, maxMainItems)
  const othersValue = items.slice(maxMainItems).reduce((sum, item) => sum + item.value, 0)

  const displayItems =
    othersValue > 0 ? [...mainItems, { label: 'その他', value: othersValue }] : mainItems

  const bgColors = [
    ...mainItems.map((_, i) => palette[i % palette.length]),
    ...(othersValue > 0 ? [OTHERS_COLOR] : [])
  ]

  const centerTextPlugin: Plugin<'doughnut'> = {
    id: 'centerText',
    afterDraw(chart) {
      const { ctx, chartArea } = chart
      const cx = (chartArea.left + chartArea.right) / 2
      const cy = (chartArea.top + chartArea.bottom) / 2
      ctx.save()
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      if (centerLabel) {
        ctx.font = '14px system-ui, sans-serif'
        ctx.fillStyle = '#34414F'
        ctx.fillText(centerLabel, cx, cy - 16)
      }
      ctx.font = 'bold 22px system-ui, sans-serif'
      ctx.fillStyle = '#1a202c'
      ctx.fillText(total.toLocaleString(), cx, centerLabel ? cy + 12 : cy)
      ctx.restore()
    }
  }

  const options: ChartOptions<'doughnut'> = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '65%',
    plugins: {
      legend: {
        display: true,
        position: 'top',
        align: 'end',
        labels: {
          color: '#34414F',
          font: { size: 12 },
          boxWidth: 12,
          boxHeight: 12,
          padding: 20,
          usePointStyle: false
        }
      },
      tooltip: {
        backgroundColor: '#ffffff',
        titleColor: '#34414F',
        titleFont: { size: 12, weight: 'bold' },
        bodyFont: { size: 13, weight: 'bold' },
        padding: 10,
        cornerRadius: 8,
        borderColor: '#E4E8EE',
        borderWidth: 1,
        displayColors: true,
        boxWidth: 12,
        boxHeight: 12,
        callbacks: {
          title: () => tooltipTitle ?? '',
          label: (context: TooltipItem<'doughnut'>) => {
            const value = context.raw as number
            const pct = total > 0 ? Math.round((value / total) * 100) : 0
            return ` ${context.label}  ${pct}%`
          },
          labelTextColor: (context) => bgColors[context.dataIndex] ?? '#34414F'
        }
      }
    }
  }

  return (
    <Doughnut
      data={{
        labels: displayItems.map((item) => item.label),
        datasets: [
          {
            data: displayItems.map((item) => item.value),
            backgroundColor: bgColors,
            borderWidth: 0
          }
        ]
      }}
      plugins={[centerTextPlugin]}
      options={options}
    />
  )
}

export default PieChart
